import * as React from 'react';
import styled from 'styled-components';

import { InfoBox } from '@longline/aqua-ui/containers/InfoBox';
import { TertiaryButton } from '@longline/aqua-ui/controls/TertiaryButton';
import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { Form } from '@longline/aqua-ui/containers/Form';
import { Slider } from '@longline/aqua-ui/inputs/Slider';
import { IGradientStop } from '@longline/aqua-ui/Types';
import { Dropdown } from '@longline/aqua-ui/inputs/Dropdown';
import { Gradient } from '@longline/aqua-ui/controls/Gradient';

import { ILayer, runLabel } from '../../types/ILayer';
import { useGlobalState } from '../../contexts/state/GlobalStateContext';
import { useConfirm } from '../../contexts/confirm/ConfirmContext';
import { LayerInfoBoxHeader } from './LayerInfoBoxHeader';
import { LayerUtils } from '../../util/LayerUtils';
import { PolygonUtils } from '../../util/PolygonUtils';
import { ELayerType } from '../../types/ELayerType';
import { Can } from '../../ui/Can';
import { LayerExporterExcel } from '../../services/LayerExporterExcel';

interface IProps {
  value?: ILayer;
}

const LayerInfoBox = (props: IProps) => {
  const globalState = useGlobalState();
  const confirm = useConfirm();

  // `value` is optional in IProps because the wrapping <ListView aux> injects
  // the active item at runtime (see IListViewProps.aux). When no item is
  // active there's nothing to show. The local `layer` alias preserves the
  // narrowing inside event-handler closures (TS doesn't narrow layer
  // across closure boundaries on its own).
  if (!props.value) return null;
  const layer = props.value;
  // Aliased for the same reason: `layer.run` would not stay narrowed inside the
  // delete handler below.
  const run = layer.run;

  const humanArea = (name: string, aream2: number) => {
    if(aream2 > 1000000) return <InfoBox.Row name={<>{name} <Unit>(km²)</Unit></>} value={<NumberFormatter value={aream2/1000000} decimals={2}/>}/>;
    return <InfoBox.Row name={<>{name} <Unit>(m²)</Unit></>} value={<NumberFormatter value={aream2} decimals={0}/>}/>;
  }

  const handleUpdateContours = (value: number) => {
    layer.contours = value;
    globalState.updateLayer(layer);
  }

  const handleUpdateContourThickness = (value: number) => {
    layer.contourThickness = value;
    globalState.updateLayer(layer);
  }

  const handleUpdateGradient = (value: IGradientStop[]) => {
    layer.gradient = value;
    globalState.updateLayer(layer);
  }

  const handleUpdateTrail = (value: number) => {
    layer.trail = value;
    globalState.updateLayer(layer);
  }

  const handleUpdateDensity = (value: number) => {
    layer.density = value;
    globalState.updateLayer(layer);
  }

  const handleUpdatePointSize = (value: number) => {
    layer.pointSize = value;
    globalState.updateLayer(layer);
  }

  const handleUpdateDelay = (value: number) => {
    layer.delay = value;
    globalState.updateLayer(layer);
  }

  // Get layer definition:
  const def = LayerUtils.layerTypeToDefinition(layer.type);

  // Get layer's min and max values.
  const [minValue, maxValue] = LayerUtils.getMinMax(layer);

  // Get layer's area in m2:
  const minLng = Math.min(...layer.data.map(p => p.lng));
  const maxLng = Math.max(...layer.data.map(p => p.lng));
  const minLat = Math.min(...layer.data.map(p => p.lat));
  const maxLat = Math.max(...layer.data.map(p => p.lat));
  const width = PolygonUtils.distance( { lng: minLng, lat: minLat }, { lng: maxLng, lat: minLat });
  const height = PolygonUtils.distance( { lng: minLng, lat: minLat }, { lng: minLng, lat: maxLat });
  const aream2 = width * height;

  return (
    <InfoBox 
      scroll
      minHeight={400}
      padded
      header={<LayerInfoBoxHeader value={layer}/>}
      footer={<>
        {/* No TOGGLE here: the eye icon in LayerInfoBoxHeader already does it,
            and the footer has room for three buttons. */}
        <TertiaryButton onClick={() => globalState.focusLayer(layer)}>FOCUS</TertiaryButton>
        <Can permission="exportLayer">
          <TertiaryButton onClick={() => LayerExporterExcel.export(layer)}>EXPORT</TertiaryButton>
        </Can>
        {run && <TertiaryButton onClick={() => confirm.start(
          () => globalState.deleteRun(run.id),
          "Delete run",
          `Delete every layer produced by ${runLabel(run)}?`
        )}>DELETE RUN</TertiaryButton>}
      </>}
    >
      {run && <InfoBox.Row name="Run" value={runLabel(run)}/>}
      <InfoBox.Row name="Source" value={layer.source}/>
      <InfoBox.Row name="Points" value={<NumberFormatter value={layer.data.length} decimals={0}/>}/>
      {humanArea("Area", aream2)}
      <InfoBox.Row 
        name={<>Range <Unit>({def.unit})</Unit></>} 
        value={<><NumberFormatter value={minValue} decimals={def.decimals}/> - <NumberFormatter value={maxValue} decimals={def.decimals}/></>}
      />

      <Spacer/>

      {layer.type != ELayerType.CurrentSpeed && <Form.Field
        label="Contour density" 
        name="contours"
        hint="Number of contour lines, or none."
        wrapper='box'
        value={layer.contours}
        onChange={handleUpdateContours}
        rules={{ required: {} }}
        // This can go all the way to 255, but that's too dense.
        control={<Slider padded min={0} max={30} color="white" />}
      />}

      {layer.type != ELayerType.CurrentSpeed && <Form.Field
        label="Contour thickness" 
        name="contourThickness"
        hint="Thickness of the contour lines."
        wrapper='box'
        value={layer.contourThickness}
        onChange={handleUpdateContourThickness}
        rules={{ required: {} }}
        control={<Slider padded min={1} max={4} color="white" />}
      />}      

      <Form.Field
        label="Gradient"
        name="gradient"
        hint="Gradient used to render this layer."
        wrapper="box"
        value={layer.gradient}
        onChange={handleUpdateGradient}
        rules={{ required: {} }}
        control={<Dropdown direction='up' data={def.gradients} label={(item) => <Gradient checkered rounded thickness={10} gradientStops={item}/>}>
          <Dropdown.Column>{(item) => <Gradient checkered rounded thickness={10} gradientStops={item}/>}</Dropdown.Column>
        </Dropdown>}
      />

      {layer.type == ELayerType.CurrentSpeed && <>
        <div style={{ display: 'flex', gap: '8px'}}>
          <Form.Field
            label="Particle trail" 
            name="trail"
            hint="Particle trail length"
            wrapper='box'
            value={layer.trail}
            onChange={handleUpdateTrail}
            weight={1}
            rules={{ required: {} }}
            control={<Slider padded min={20} max={80} ticks={7} snapToTicks color="white"/>}
          />
          <Form.Field
            label="Particle density" 
            name="density"
            hint="Number of particles."
            wrapper='box'
            value={layer.density}
            onChange={handleUpdateDensity}
            weight={1}
            rules={{ required: {} }}
            control={<Slider padded min={10} max={80} ticks={8} snapToTicks color="white"/>}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px'}}>
          <Form.Field
            label="Particle size" 
            name="size"
            hint="Particle size (pixels)."
            wrapper='box'
            weight={1}
            value={layer.pointSize}
            onChange={handleUpdatePointSize}
            rules={{ required: {} }}
            control={<Slider padded min={1} max={10} ticks={11} snapToTicks color="white"/>}
          />      

          <Form.Field
            label="Particle delay" 
            name="delay"
            hint="Simulation delay."
            wrapper='box'
            weight={1}
            value={layer.delay}
            onChange={handleUpdateDelay}
            rules={{ required: {} }}
            control={<Slider padded min={0} max={10} ticks={11} snapToTicks color="white"/>}
          />
        </div>
      </>}   
    </InfoBox>
  )
}

const Unit = styled.span`
  display: inline-block;
  font-size: 80%;
  font-weight: normal;
  color: #eee;
`

const Spacer = styled.div`
  height: 16px;  
`

export { LayerInfoBox }
